import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, ref, watch } from 'vue'

import { getScreenShareVideoConstraints } from '@/shared/utils/webrtc'

import { useStoreMediaDevices } from '../store'

const applyDetailContentHint = (stream: MediaStream) => {
  const [videoTrack] = stream.getVideoTracks()
  if (videoTrack) {
    videoTrack.contentHint = 'detail'
  }
}

export const useLocalScreenSharePreview = () => {
  const previewElement = ref<HTMLVideoElement>()
  const currentStream = ref<MediaStream | null>(null)
  const storeMediaDevices = useStoreMediaDevices()
  const { screenShareQuality } = storeToRefs(storeMediaDevices)

  let onTrackEnded: (() => void) | undefined

  const isDisplayMediaSupported = computed(() => {
    return typeof navigator.mediaDevices?.getDisplayMedia === 'function'
  })

  const isPreviewActive = computed(() => {
    const track = currentStream.value?.getVideoTracks().at(0)
    return !!track && track.readyState === 'live'
  })

  const unbindEnded = (stream: MediaStream | null) => {
    const track = stream?.getVideoTracks().at(0)
    if (track && onTrackEnded) {
      track.removeEventListener('ended', onTrackEnded)
    }
    onTrackEnded = undefined
  }

  const stopPreview = () => {
    const stream = currentStream.value
    currentStream.value = null
    unbindEnded(stream)
    if (previewElement.value) {
      previewElement.value.srcObject = null
    }
    if (stream) {
      storeMediaDevices.closeStream(stream)
    }
  }

  const bindEnded = (stream: MediaStream) => {
    const track = stream.getVideoTracks().at(0)
    if (!track) return
    onTrackEnded = () => {
      stopPreview()
    }
    track.addEventListener('ended', onTrackEnded)
  }

  const startPreview = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      throw new Error('getDisplayMedia is not supported')
    }

    stopPreview()

    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: getScreenShareVideoConstraints(screenShareQuality.value),
      audio: false,
    })
    applyDetailContentHint(stream)
    currentStream.value = stream
    bindEnded(stream)
    if (previewElement.value) {
      previewElement.value.srcObject = stream
    }
    return stream
  }

  watch(screenShareQuality, (quality) => {
    const track = currentStream.value?.getVideoTracks().at(0)
    if (!track || track.readyState !== 'live') return
    void track.applyConstraints(getScreenShareVideoConstraints(quality)).catch(() => undefined)
  })

  onBeforeUnmount(() => {
    stopPreview()
  })

  return {
    previewElement,
    isDisplayMediaSupported,
    isPreviewActive,
    startPreview,
    stopPreview,
  }
}
